import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {BsModalService} from 'ngx-bootstrap/modal';
import {ShopDataService} from '@webu/data';
import {Subject, catchError, concatMap, filter, map, of, take, takeUntil} from 'rxjs';
import {PlanBillingType, PlanSummary, Profile, Vehicle} from '@ox/typings';
import {DowngradeModalComponent} from '../../components';
import {Store} from '@ngrx/store';
import {selectActiveVehicle, selectProfile} from '@ox/data';
import {AutoDestroy} from '@ox/utils';

/** Service for formatting payment methods */
@Injectable()
export class CheckoutNavigationService {

  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  activeVehicle$ = this.store.select(selectActiveVehicle)
    .pipe(
      takeUntil(this.destroy$),
      filter((vehicle): vehicle is Vehicle => !!vehicle));

  profile$ = this.store.select(selectProfile)
    .pipe(
      takeUntil(this.destroy$),
      filter((profile): profile is Profile => !!profile));

  constructor(
    private router: Router,
    private modalService: BsModalService,
    private shopDataService: ShopDataService,
    private store: Store
  ) {}

  /** Calls the /quote API and goes to checkout unless the downgrade modal needs to be shown */
  goToCheckout(planCodes: string[], prevPage: string, error: string, billingType?: PlanBillingType, defaultSelected?: PlanBillingType, page?: string) {
    return this.activeVehicle$
      .pipe(
        take(1),
        concatMap((vehicle) => this.shopDataService.getPlanSummary(planCodes, vehicle.vin, billingType, true, page)),
        map((planSummary) => {
          if (planSummary.downgrade) {
            this.displayDowngradeModal(planCodes, planSummary);
          } else {
            this.navigateToCheckoutUrl(planCodes, prevPage, error, billingType, defaultSelected);
          }
        }),
        catchError((err) => {
          // Navigate to the checkout screen even if there is an error so the error can be displayed there AND it can try the API again
          console.error(err);
          this.navigateToCheckoutUrl(planCodes, prevPage, error, billingType, defaultSelected);
          return of(null);
        })
      );
  }

  private displayDowngradeModal(planCodes: string[], planSummary: PlanSummary) {
    this.modalService.show(DowngradeModalComponent, {
      initialState: {
        planCodes: planCodes,
        planSummary: planSummary
      },
      class: 'modal-lg anchor-modal-bottom'
    });
  }

  private navigateToCheckoutUrl(planCodes: string[], prevPage: string, error: string, billingType?: PlanBillingType, defaultSelected?: PlanBillingType) {
    this.router.navigate(['./shop/checkout'], {
      queryParams: {
        planCodes: planCodes,
        prevPage: prevPage,
        error: error, 
        billingType: billingType,
        defaultSelected: defaultSelected
      }
    });
  }
}
